<template>
    <div class="vuetify-wrapper v-application">
        <v-card class="v-application">
            <v-card-title class="headline">
                Terminanfragen pro Datum
            </v-card-title>

            <v-card-text class="pb-0 px-0 v-application--is-ltr">
                <v-container fluid>
                    <v-row>
                        <v-col cols="12" md="auto">
                            <v-chip-group @change="changeChip" active-class="indigo--text text--accent-4" mandatory>
                                <v-chip>Heute</v-chip>
                                <v-chip>Letzten Woche</v-chip>
                                <v-chip>letzten Monat</v-chip>
                            </v-chip-group>
                        </v-col>
                        <v-col cols="12" md="auto">
                            <v-menu
                                v-model="isDateMenuOpen"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attr }">
                                    <v-text-field
                                        v-model="dateRangeText"
                                        v-on="on"
                                        v-bind="attrs"
                                        required
                                        readonly
                                        hide-details
                                        prepend-icon="mdi-calendar"
                                        class="pt-0"
                                        label="Datumsbereich"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="dateRange"
                                    range
                                    no-title
                                    locale="de-DE"
                                    :first-day-of-week="1"
                                ></v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col cols="12" lg="6">
                            <v-row class="py-0">
                                <v-col v-if="activeTab !== 1" cols="12" md="6">
                                    <v-select
                                        v-model="filters.wf_state"
                                        :items="appointmentStateOptions"
                                        clearable
                                        label="Zustand"
                                        dense
                                        hide-details
                                    />
                                </v-col>

                                <v-col v-if="activeTab !== 1" cols="12" md="6">
                                    <v-select
                                        v-model="filters.commited"
                                        className="committed-filter"
                                        :items="committedStateOptions"
                                        clearable
                                        dense
                                        label="zugesagte / nicht-zugesagte"
                                        hide-details
                                    />
                                </v-col>

                                <v-col cols="12" md="6">
                                    <v-select
                                        v-model="filters.service"
                                        :items="serviceTypeOptions"
                                        clearable
                                        label="Apotheker / PTA / PKA"
                                        dense
                                        hide-details
                                    />
                                </v-col>

                                <v-col cols="12" md="6">
                                    <v-select
                                        v-model="filters.nightshift"
                                        :items="isNightShiftOptions"
                                        clearable
                                        label="Nachtdienst / keine Nachtdienst"
                                        dense
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-select
                                        v-model="filters.has_request"
                                        :items="hasRequestOptions"
                                        clearable
                                        label="Von FB angefragt / Von FB Nicht angefragt "
                                        dense
                                        hide-details
                                    />
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>

            <v-card-text v-if="requestedAppointments">
                <v-tabs
                    v-model="activeTab"
                    :color="tabsConfig[activeTab].color"
                >
                    <v-tab v-for="(tab, idx) in tabsConfig" :key="tab.key">
                        {{ tab.label }}
                        <v-chip
                            class="chip-small ml-2"
                            :color="activeTab == idx && tab.chipColor"
                            outlined
                            small
                        >
                            {{ tabCount(requestedAppointments[tab.key]) }}
                        </v-chip>
                    </v-tab>
                </v-tabs>


                <v-tabs-items v-model="activeTab">
                    <v-tab-item v-for="(tab, idx) in tabsConfig" :key="tab.key" :value="idx">
                        <v-card :disabled="isLoading" flat>
                            <v-card-text class="pb-0">
                                <AppointmentsRequestsPerDateTable
                                    :requestsData="requestedAppointments[tab.key]"
                                    :isPharmacyRequest="tab.isPharmacyRequest"
                                    :isCanceledRequest="tab.isCanceledRequest"
                                    :isLoading="isLoading"
                                    @fetch-requests="fetchRequestedAppointments"
                                />
                            </v-card-text>
                        </v-card>
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>
            <v-card-text v-else class="text-center">
                <v-skeleton-loader max-width="700" max-height="200" type="table-thead, table-row@2"></v-skeleton-loader>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import axios from "axios"
import { mapMutations } from "vuex";
import AppointmentsRequestsPerDateTable from '../components/appointments/AppointmentsRequestsPerDateTable.vue'
import { filters as appointmentRequestsFilters } from "../constants";


const TABSCONFIG = [
    {
        key: 'pharmacy_open',
        idx: 0,
        label: 'Apotheken (offen)',
        color: 'green darken-1',
        chipColor: 'green',
        isPharmacyRequest: true,
        responseKey: 'pharmacy',
        params: { pharmacies: 1, pharmacy_assignment: 'unassigned', sort_by: 'created' }
    },
    {
        key: 'pharmacy_assigned',
        idx: 1,
        label: 'Apotheken (vergeben)',
        color: 'orange darken-1',
        chipColor: 'orange',
        isPharmacyRequest: true,
        responseKey: 'pharmacy',
        params: { pharmacies: 1, pharmacy_assignment: 'assigned', sort_by: 'created' }
    },
    {
        key: 'freelancer',
        idx: 2,
        label: 'FB-Anfragen',
        color: 'purple darken-1',
        chipColor: 'purple',
        isPharmacyRequest: false,
        responseKey: 'freelancer',
        params: { freelancers: 1, sort_by: 'request_date' }
    },
    {
        key: 'canceled',
        idx: 3,
        label: 'Stornierte Termine',
        color: 'red darken-1',
        chipColor: 'red',
        isPharmacyRequest: false,
        isCanceledRequest: true,
        responseKey: 'canceled',
        params: { canceled: 1, sort_by: 'last_fb_cancelled_at' }
    }
]

export default {
    components: {
        AppointmentsRequestsPerDateTable
    },
    data() {
        return {
            activeTab: 0,
            isDateMenuOpen: false,
            dateRange: [],
            filters: {
                wf_state: null,
                service: null,
                commited: null,
                nightshift: null,
                has_request: null,
            },
            requestedAppointments: {},
            isLoading: false,
            intervalId: null,
            tabsConfig: TABSCONFIG,
            appointmentStateOptions: appointmentRequestsFilters.appointmentWfStatesOptions,
            committedStateOptions: appointmentRequestsFilters.committedStatesOptions,
            serviceTypeOptions: appointmentRequestsFilters.servicesTypesOptions,
            isNightShiftOptions: appointmentRequestsFilters.isNightShiftOptions,
            hasRequestOptions: appointmentRequestsFilters.hasRequestOptions
        }
    },
    methods: {
        async fetchRequestedAppointments({
            page = 1,
            page_size = 25,
            sort_by = null,
            sort_asc = -1,
            tab = null,
        }) {
            const activeTab = tab || this.tabsConfig[this.activeTab];
            if(!sort_by){
                sort_by = activeTab.params?.sort_by || (activeTab.isPharmacyRequest ? 'created' : 'request_date');
            }
            const request_params = {
                ...activeTab.params,
                page,
                page_size,
                sort_asc,
                sort_by: sort_by,
                start_date: this.dateRange[0],
                end_date: this.dateRange[1],
                ...this.filters
            }

            try {
                this.isLoading = true;
                const response = await axios.get('/api/appointments/request_overview', {
                    params: request_params,
                });
                const responsePayload = response.data?.[activeTab.responseKey] || {};
                const normalizedPayload = {
                    total_results: responsePayload.total_results || 0,
                    appointment_total_results: responsePayload.appointment_total_results ?? responsePayload.total_results ?? 0,
                    page: responsePayload.page || page,
                    has_next_page: !!responsePayload.has_next_page,
                    appointments: responsePayload.appointments || [],
                };
                this.requestedAppointments = {
                    ...this.requestedAppointments,
                    [activeTab.key]: normalizedPayload,
                };
            } catch (error) {
                this.alertError();
            } finally {
                this.isLoading = false;
            }
        },
        tabCount(payload) {
            return payload?.appointment_total_results ?? payload?.total_results ?? 0;
        },
        changeChip(chipNumber) {
            if (chipNumber === undefined) return;

            const today = new Date();
            let start, end = today;

            switch (chipNumber) {
                case 0: // today
                    start = today;
                    end = today;
                    break;

                case 1: // last week
                    const lastWeek = new Date(today);
                    lastWeek.setDate(today.getDate() - 7);
                    start = lastWeek;
                    end = today;
                    break;

                case 2: // last month
                    const lastMonth = new Date(today);
                    lastMonth.setMonth(today.getMonth() - 1);
                    start = lastMonth;
                    end = today;
                    break;

                default:
                    break;
            }
            this.dateRange = [this.format(start), this.format(end)];
        },
        format(date) {
            return date.toLocaleDateString('en-CA');
        },
        ...mapMutations({
            alertError: 'alert/error',
        }),
    },
    computed: {
        dateRangeText() {
            return this.dateRange.map(date => (
                new Date(date).toLocaleDateString('de-DE')
            )).join(' - ')
        },
    },
    async created() {
        ['dateRange', 'activeTab', 'filters'].forEach(key => {
            this.$watch(
                key,
                async () => await this.fetchRequestedAppointments({}),
                { deep: true }
            );
        });
        for (const tab of this.tabsConfig.slice(1)) {
            await this.fetchRequestedAppointments({ tab });
        }

        this.intervalId = setInterval(() => {
            this.fetchRequestedAppointments({});
        }, 10 * 60 * 1000);
    },
    beforeDestroy() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    },
};
</script>

<style scoped></style>
